import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChatRefreshMarker } from './useChatHistory';

export type ChatOutgoingPayload =
  | { type: 'text'; data: string }
  | { type: 'audio'; data: File };

/** A sent message shown until a later server refresh can contain it. */
export type OutgoingChatMessage = {
  clientId: string;
  createdAt: string;
  /** The latest-page request already started when the message was sent. */
  createdAfterRequestId: number;
  text: string | null;
  audio: { name: string; url: string } | null;
};

export type UseOutgoingMessagesOptions = {
  sessionId: string;
  canSend: boolean;
  successfulRefresh: ChatRefreshMarker | undefined;
  getLatestStartedRequestId: () => number;
  send: (payload: ChatOutgoingPayload) => Promise<unknown>;
  onSendStarted?: (clientId: string) => void;
  onSendFailed?: (clientId: string, error: unknown) => void;
};

const releaseAudio = (message: OutgoingChatMessage) => {
  if (message.audio) {
    URL.revokeObjectURL(message.audio.url);
  }
};

/**
 * Local rows for sent messages. A row retires on the first successful
 * latest-page refresh that started after the send, or when the send fails.
 */
export const useOutgoingMessages = ({
  sessionId,
  canSend,
  successfulRefresh,
  getLatestStartedRequestId,
  send,
  onSendStarted,
  onSendFailed,
}: UseOutgoingMessagesOptions) => {
  const [localMessages, setLocalMessages] = useState<OutgoingChatMessage[]>([]);
  const localMessagesRef = useRef<OutgoingChatMessage[]>([]);
  const activeRef = useRef(true);
  const submittingRef = useRef(false);
  const nextClientIdRef = useRef(0);

  // Retire visible rows in the same render that receives server data.
  // The effect below only releases their resources and stored local state.
  const visibleLocalMessages = useMemo(
    () =>
      successfulRefresh?.sessionId === sessionId
        ? localMessages.filter(
            message =>
              message.createdAfterRequestId >= successfulRefresh.requestId
          )
        : localMessages,
    [localMessages, successfulRefresh, sessionId]
  );

  useLayoutEffect(() => {
    activeRef.current = true;

    return () => {
      activeRef.current = false;
      localMessagesRef.current.forEach(releaseAudio);
      localMessagesRef.current = [];
    };
  }, []);

  const replaceLocalMessages = useCallback((next: OutgoingChatMessage[]) => {
    const retainedIds = new Set(next.map(message => message.clientId));
    localMessagesRef.current.forEach(message => {
      if (!retainedIds.has(message.clientId)) {
        releaseAudio(message);
      }
    });
    localMessagesRef.current = next;
    if (activeRef.current) {
      setLocalMessages(next);
    }
  }, []);

  useEffect(() => {
    if (successfulRefresh?.sessionId !== sessionId) {
      return;
    }

    const next = localMessagesRef.current.filter(
      message => message.createdAfterRequestId >= successfulRefresh.requestId
    );
    if (next.length !== localMessagesRef.current.length) {
      replaceLocalMessages(next);
    }
  }, [successfulRefresh, sessionId, replaceLocalMessages]);

  /** Resolves to whether the server accepted the message. */
  const sendMessage = async (
    payload: ChatOutgoingPayload
  ): Promise<boolean> => {
    if (!activeRef.current || submittingRef.current || !canSend) {
      return false;
    }

    submittingRef.current = true;
    const message: OutgoingChatMessage = {
      clientId: `${sessionId}:${++nextClientIdRef.current}`,
      createdAt: new Date().toISOString(),
      createdAfterRequestId: getLatestStartedRequestId(),
      text: payload.type === 'text' ? payload.data : null,
      audio:
        payload.type === 'audio'
          ? { name: payload.data.name, url: URL.createObjectURL(payload.data) }
          : null,
    };
    replaceLocalMessages([message, ...localMessagesRef.current]);
    onSendStarted?.(message.clientId);

    try {
      await send(payload);
      return activeRef.current;
    } catch (error) {
      if (activeRef.current) {
        replaceLocalMessages(
          localMessagesRef.current.filter(
            current => current.clientId !== message.clientId
          )
        );
        onSendFailed?.(message.clientId, error);
      }
      return false;
    } finally {
      submittingRef.current = false;
    }
  };

  return { localMessages: visibleLocalMessages, sendMessage };
};
